/**
 * Aplica una ficha de la Guía Legal al formulario de Concepto de Nómina.
 *
 * Lo usan AgregarConceptoNomina y EditarConceptoNomina, que son los dos que tienen un
 * Form donde escribir. Vive aquí y no dentro de cada pantalla para que las dos hagan
 * exactamente lo mismo.
 *
 * Escribe sólo en el formulario: nada se guarda hasta que el usuario le da a Guardar.
 */
import React from "react";
import { Modal, Typography, message } from "antd";
import axios from "axios";
import { normalizar } from "./GuiaConceptosLey/catalogoConceptosLey";

const { Text, Paragraph } = Typography;

// slot de la ficha → sufijo del nombre de campo en el Form
export const CAMPO_DE_SLOT = {
    total: "ImporteTotal",
    1: "Importe1",
    2: "Importe2",
    3: "Importe3",
    4: "Importe4",
};

// Sólo estos tres importes llevan acumulados fiscales en la ficha.
const ACUM_DE_SLOT = { total: "total", 1: "importe1", 2: "importe2" };

/**
 * Las únicas banderas que el usuario puede prender a mano en las dos pantallas. El resto
 * de las que menciona la ficha (esAguinaldo, esSueldo, esPrimaVacacional, las de INFONAVIT
 * y las de separación) son punteros únicos por empresa que el motor resuelve con
 * find/findOne: prenderlas desde aquí haría que el cálculo tomara el concepto equivocado
 * sin avisar. Se reportan como pendientes, no se aplican.
 */
export const BANDERAS_APLICABLES = ["esPercepcionArt174", "esHorasExtra"];

export const ETIQUETA_BANDERA = {
    esPercepcionArt174: "Percepción Art. 174",
    esHorasExtra: "Es Horas Extra",
};

export const etiquetaSlot = (slot) =>
    slot === "total" ? "Importe Total" : `Importe ${slot}`;

export const etiquetaFormula = (f) =>
    f.nombre ? `${etiquetaSlot(f.slot)} — ${f.nombre}` : etiquetaSlot(f.slot);

// El Drawer se abre en 1300; el modal de confirmación tiene que quedar por encima.
const Z_INDEX_CONFIRMA = 1400;

/**
 * Resuelve nombres de TipoAcumulado a sus _id de la empresa. Devuelve un Map; los que no
 * existen simplemente no aparecen y el llamador los reporta.
 */
const resolverAcumulados = async (nombres, empresaId, headersToken) => {
    const mapa = new Map();
    await Promise.all(
        nombres.map(async (nombre) => {
            try {
                const res = await axios.post(
                    "tiposAcumulados/selectTiposAcumuladosItem",
                    { empresaId, query: nombre },
                    { headers: headersToken }
                );
                if (res.data.success !== 2) return;
                // El endpoint busca por regex y regresa hasta 10: hay que quedarse con el
                // que se llama exactamente así, no con el primero que devuelva.
                const exacto = (res.data.tiposAcumulados || []).find(
                    (t) => normalizar(t.nombre) === normalizar(nombre)
                );
                if (exacto) mapa.set(nombre, exacto._id);
            } catch (e) {
                // Se queda sin resolver y se reporta como pendiente.
            }
        })
    );
    return mapa;
};

// Modal de confirmación cuando hay campos capturados que se van a reemplazar.
const confirmarReemplazo = (concepto, pisa, resumen) =>
    new Promise((resolve) => {
        Modal.confirm({
            title: `Aplicar "${concepto.nombre}" al concepto`,
            width: 640,
            zIndex: Z_INDEX_CONFIRMA,
            okText: "Reemplazar",
            cancelText: "Cancelar",
            content: (
                <div>
                    <Paragraph style={{ marginBottom: 8 }}>
                        Se van a reemplazar {pisa.length}{" "}
                        {pisa.length === 1 ? "campo que ya tiene" : "campos que ya tienen"} algo
                        capturado:
                    </Paragraph>
                    <div
                        style={{
                            maxHeight: 220,
                            overflowY: "auto",
                            border: "1px solid #f0f0f0",
                            borderRadius: 4,
                            padding: 8,
                            marginBottom: 10,
                        }}
                    >
                        {pisa.map((p, i) => (
                            <div key={i} style={{ marginBottom: 8 }}>
                                <Text strong style={{ fontSize: 12 }}>
                                    {p.etiqueta}
                                </Text>
                                <div
                                    style={{
                                        fontFamily: "monospace",
                                        fontSize: 11,
                                        color: "rgba(0,0,0,0.65)",
                                        whiteSpace: "pre-wrap",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    {p.valor}
                                </div>
                            </div>
                        ))}
                    </div>
                    <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                        {resumen} Nada se guarda hasta que le des a Guardar.
                    </Paragraph>
                </div>
            ),
            onOk: () => resolve(true),
            onCancel: () => resolve(false),
        });
    });

// Reporte final: si todo salió limpio basta un toast; si hay pendientes hay que leerlos.
const reportar = (concepto, aplicados, notas) => {
    if (notas.length === 0) {
        message.success(
            `Se ${aplicados === 1 ? "aplicó" : "aplicaron"} ${aplicados} ${
                aplicados === 1 ? "campo" : "campos"
            } de "${concepto.nombre}". Revisa y guarda.`,
            5
        );
        return;
    }
    Modal.info({
        title: `Se aplicó "${concepto.nombre}"`,
        width: 640,
        zIndex: Z_INDEX_CONFIRMA,
        okText: "Entendido",
        content: (
            <div>
                <Paragraph style={{ marginBottom: 8 }}>
                    Se {aplicados === 1 ? "escribió" : "escribieron"} {aplicados}{" "}
                    {aplicados === 1 ? "campo" : "campos"} en el formulario. Quedan cosas que
                    tienes que revisar a mano:
                </Paragraph>
                <ul style={{ paddingLeft: 18, fontSize: 13, marginBottom: 8 }}>
                    {notas.map((n, i) => (
                        <li key={i} style={{ marginBottom: 6 }}>
                            {n}
                        </li>
                    ))}
                </ul>
                <Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
                    Nada se guarda hasta que le des a Guardar.
                </Paragraph>
            </div>
        ),
    });
};

/**
 * Escribe la ficha en el formulario.
 *
 * @param formRef          ref al Form de la pantalla
 * @param concepto         la ficha del catálogo
 * @param formulas         las fórmulas a aplicar (todas, o una sola desde el botón "Usar")
 * @param todo             true = "Aplicar todo": además intenta banderas y clave SAT
 * @param tipoFormulario   el Tipo capturado en el form (1 Percepción / 2 Deducción / 3 Obligación)
 * @param satIdParaClave   _id del TipoConceptoSat que corresponde a la ficha, si la pantalla lo encontró
 * @param onAplicado       callback con lo que la pantalla necesita espejear en su state
 */
export async function aplicarConceptoLey({
    formRef,
    concepto,
    formulas,
    todo,
    empresaId,
    headersToken,
    tipoFormulario,
    satIdParaClave,
    onAplicado,
}) {
    const form = formRef && formRef.current;
    if (!form || !formulas || formulas.length === 0) return;

    const actuales = form.getFieldsValue();

    // ── 1. Qué se va a pisar ────────────────────────────────────────────────
    const pisa = [];
    for (const f of formulas) {
        const suf = CAMPO_DE_SLOT[f.slot];
        if (!suf) continue;
        const formulaActual = actuales[`formula${suf}`];
        const nombreActual = actuales[`nombre${suf}`];
        if (formulaActual && formulaActual !== f.formula) {
            pisa.push({ etiqueta: `${etiquetaFormula(f)} · fórmula`, valor: formulaActual });
        }
        if (f.nombre && nombreActual && nombreActual !== f.nombre) {
            pisa.push({ etiqueta: `${etiquetaFormula(f)} · nombre`, valor: nombreActual });
        }
    }

    const conAcumulados =
        !!concepto.acumulados &&
        formulas.some((f) => ACUM_DE_SLOT[f.slot] && concepto.acumulados[ACUM_DE_SLOT[f.slot]]);
    const resumen =
        `Se aplican ${formulas.length} ${formulas.length === 1 ? "fórmula" : "fórmulas"}` +
        (conAcumulados ? ", sus acumulados fiscales" : "") +
        (todo ? " y las banderas que apliquen." : ".");

    if (pisa.length > 0) {
        const seguir = await confirmarReemplazo(concepto, pisa, resumen);
        if (!seguir) return;
    }

    // ── 2. Armar los valores ────────────────────────────────────────────────
    const valores = {};
    let aplicados = 0;
    const notas = [];

    for (const f of formulas) {
        const suf = CAMPO_DE_SLOT[f.slot];
        if (!suf) continue;
        valores[`formula${suf}`] = f.formula;
        aplicados++;
        if (f.nombre) {
            valores[`nombre${suf}`] = f.nombre;
            aplicados++;
        }
    }

    // ── 3. Acumulados fiscales ──────────────────────────────────────────────
    if (conAcumulados) {
        const pedidos = [];
        for (const f of formulas) {
            const llave = ACUM_DE_SLOT[f.slot];
            const lista = llave && concepto.acumulados[llave];
            if (lista) pedidos.push(...lista);
        }
        const mapa = await resolverAcumulados([...new Set(pedidos)], empresaId, headersToken);
        const noEncontrados = [];
        for (const f of formulas) {
            const llave = ACUM_DE_SLOT[f.slot];
            const lista = llave && concepto.acumulados[llave];
            if (!lista) continue;
            const ids = [];
            for (const nombre of lista) {
                const id = mapa.get(nombre);
                if (id) ids.push(id);
                else noEncontrados.push(nombre);
            }
            if (ids.length > 0) {
                valores[`acumuladosFiscales${CAMPO_DE_SLOT[f.slot]}`] = ids;
                aplicados++;
            }
        }
        if (noEncontrados.length > 0) {
            notas.push(
                `No encontré estos acumulados en tu catálogo: ${[
                    ...new Set(noEncontrados),
                ].join(", ")}. Créalos en Panel → Tipos de Acumulados y ponlos a mano, ` +
                    "o la exención se va a volver a otorgar completa en cada pago."
            );
        }
    }

    // ── 4. Banderas y clave SAT (sólo en "Aplicar todo") ────────────────────
    if (todo) {
        const banderasFicha = (concepto.captura && concepto.captura.banderas) || [];
        const prendidas = [];
        for (const b of banderasFicha) {
            if (!BANDERAS_APLICABLES.includes(b)) continue;
            // El switch de Art. 174 sólo existe en las percepciones. Escribirlo en una
            // deducción dejaría el campo puesto sin que nadie lo pueda ver ni quitar.
            if (b === "esPercepcionArt174" && tipoFormulario !== 1) {
                notas.push(
                    `${ETIQUETA_BANDERA[b]} no se aplicó: sólo existe cuando el Tipo es Percepción.`
                );
                continue;
            }
            valores[b] = true;
            prendidas.push(ETIQUETA_BANDERA[b] || b);
            aplicados++;
        }
        if (prendidas.length > 0) {
            // Estos switches viven en otra pestaña que el usuario puede no estar viendo.
            notas.push(
                `Se prendió ${prendidas.join(" y ")}. Revísalo antes de guardar: cambia cómo se ` +
                    "retiene el ISR del concepto."
            );
        }
        const reservadas = banderasFicha.filter((b) => !BANDERAS_APLICABLES.includes(b));
        if (reservadas.length > 0) {
            notas.push(
                `Estas banderas las asigna el sistema y no se aplican desde aquí: ${reservadas.join(
                    ", "
                )}. Son marcas únicas por empresa; si de verdad este concepto debe traerlas, ` +
                    "hay que quitárselas antes al concepto que las tenga."
            );
        }

        if (satIdParaClave) {
            if (!actuales.tipoConceptoSatId) {
                valores.tipoConceptoSatId = satIdParaClave;
                aplicados++;
            } else if (actuales.tipoConceptoSatId !== satIdParaClave) {
                notas.push(
                    `El Tipo Concepto SAT ya tenía otra clave capturada y no se tocó. La de esta ` +
                        `ficha es la ${concepto.clave}.`
                );
            }
        }
    }

    // ── 5. Escribir ─────────────────────────────────────────────────────────
    form.setFieldsValue(valores);
    reportar(concepto, aplicados, notas);

    if (onAplicado) {
        onAplicado({
            tipoConceptoSatId: valores.tipoConceptoSatId,
            todo: !!todo,
        });
    }
}
